import axios from 'axios';
import { getApiBaseUrl } from '../../config/apiBase.js';

const API_BASE_URL = getApiBaseUrl();

const getAuthToken = () => localStorage.getItem('token');

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Paths include `/api/...` intentionally.
 * Staging frontend nginx uses `proxy_pass http://backend:3000/;` which strips one
 * `/api` prefix. Matching older clients (auth/team/kb) that call `/api/api/...`
 * so Express still receives `/api/web-analytics/...`.
 */
export const getWebAnalyticsSummary = async (days = 30) => {
  const response = await apiClient.get('/api/web-analytics/summary', {
    params: { days },
  });
  return response.data;
};

export const getWebAnalyticsPageDetails = async (path, days = 30) => {
  const response = await apiClient.get('/api/web-analytics/page-details', {
    params: { path, days },
  });
  return response.data;
};

export const getWebAnalyticsCountries = async (days = 30, limit = 10) => {
  const response = await apiClient.get('/api/web-analytics/countries', {
    params: { days, limit },
  });
  return response.data;
};

export const getWebAnalyticsCountryDetails = async (countryCode, params = {}) => {
  const response = await apiClient.get(
    `/api/web-analytics/countries/${encodeURIComponent(countryCode)}`,
    { params }
  );
  return response.data;
};

export const getWebAnalyticsBrowsers = async (days = 30, limit = 10) => {
  const response = await apiClient.get('/api/web-analytics/browsers', {
    params: { days, limit },
  });
  return response.data;
};

/**
 * Download export file (csv | xlsx | pdf).
 */
export const exportWebAnalyticsReport = async (format, params = {}) => {
  const token = getAuthToken();
  const query = new URLSearchParams({ format, ...params });
  const response = await axios.get(
    `${API_BASE_URL}/api/web-analytics/export?${query.toString()}`,
    {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      responseType: 'blob',
    }
  );

  const ext = format === 'xlsx' ? 'xlsx' : format;
  const rangeSuffix = params.from && params.to
    ? `${params.from}_${params.to}`
    : `${params.days || 30}d`;
  const stamp = new Date().toISOString().slice(0, 10);
  const filename = `web-analytics-${rangeSuffix}-${stamp}.${ext}`;

  const blob = new Blob([response.data], {
    type: response.headers['content-type'] || 'application/octet-stream',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
